/*
  Поведение шапки страницы «Сертификаты».

  1. Кнопка с data-certificates-scroll ведёт на якорь каталога на этой же
     странице: вместо прыжка браузера прокручиваем плавно через Lenis (он ведёт
     скролл всей страницы), отступ берём из scroll-margin-top цели, там уже
     учтена липкая шапка. Без Lenis - нативный scrollIntoView.
  2. С флагом data-certificates-snap шапка страницы и каталог ведут себя как два
     экрана fullpage-сайта: колесо сразу перелистывает к каталогу или обратно,
     свайп на телефоне дотягивается до ближайшего по направлению экрана после
     остановки инерции. Раскладку это не трогает, только прокрутку. Клавиатурный
     скролл и скролл от фокуса не трогаем, иначе сфокусированная кнопка уедет с экрана.

  Слушатели висят на document/window: в редакторе темы секция перерисовывается
  целиком, а обработчики перерисовку переживают. Флаг на window защищает от
  повторной подписки, если секция стоит на странице дважды.
*/

(() => {
  if (window.certificatesHeroBound) return;
  window.certificatesHeroBound = true;

  const TRIGGER_SELECTOR = '[data-certificates-scroll]';
  const SNAP_SELECTOR = '[data-certificates-snap]';
  const CATALOG_ID = 'certificates-catalog';
  const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';
  /*
    Без duration Lenis крутит через lerp: рывок в начале и долгое доползание
    по пикселю в конце, на телефоне это читается как тормоза. Явная длительность
    переключает его на easing по времени, cubic in-out даёт мягкий разгон и посадку.
  */
  const SCROLL_DURATION_SECONDS = 0.9;
  const easeInOutCubic = (t) => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2);
  /** Пауза без scroll-событий, после которой прокрутка считается остановившейся */
  const SETTLE_MS = 140;
  /** Ближе этого к точке привязки уже стоим на ней, дотягивать нечего */
  const SNAP_TOLERANCE_PX = 2;
  const PAGE_TOP = 0;

  const reduceMotion = () => window.matchMedia(REDUCED_MOTION_QUERY).matches;
  // В редакторе темы мерчант листает страницу, чтобы править секции: перелистывание там только мешает
  const inThemeEditor = () => Boolean(window.Shopify && window.Shopify.designMode);
  const snapEnabled = () =>
    Boolean(document.querySelector(SNAP_SELECTOR)) && !reduceMotion() && !inThemeEditor();

  /** @returns {number} отступ сверху из scroll-margin-top, там учтена липкая шапка */
  const scrollMargin = (target) => {
    const margin = parseFloat(getComputedStyle(target).scrollMarginTop);
    return Number.isNaN(margin) ? 0 : margin;
  };

  /**
   * @param {number|HTMLElement} target позиция страницы или элемент
   * @param {number} offset смещение для элемента, отрицательное останавливает выше
   */
  const smoothScrollTo = (target, offset) => {
    if (window.lenis) {
      window.lenis.scrollTo(target, {
        offset,
        immediate: reduceMotion(),
        duration: SCROLL_DURATION_SECONDS,
        easing: easeInOutCubic,
      });
      return;
    }
    const behavior = reduceMotion() ? 'auto' : 'smooth';
    if (typeof target === 'number') {
      window.scrollTo({ top: target, behavior });
      return;
    }
    target.scrollIntoView({ behavior, block: 'start' });
  };

  document.addEventListener('click', (event) => {
    const trigger = event.target.closest(TRIGGER_SELECTOR);
    if (!trigger) return;

    const targetId = decodeURIComponent((trigger.hash || '').slice(1));
    const target = targetId ? document.getElementById(targetId) : null;
    // Якоря на странице нет - пусть браузер идёт по ссылке как обычно
    if (!target) return;

    event.preventDefault();
    // Фокус переносим до скролла и без прокрутки, иначе браузер прыгнет к цели сам
    target.focus({ preventScroll: true });
    smoothScrollTo(target, -scrollMargin(target));
  });

  /** @returns {number|null} позиция страницы, на которой каталог встаёт под шапку */
  const catalogSnapY = () => {
    const catalog = document.getElementById(CATALOG_ID);
    if (!catalog) return null;
    return catalog.getBoundingClientRect().top + window.scrollY - scrollMargin(catalog);
  };

  /** Куда сейчас летим: повторное колесо в ту же сторону не перезапускает анимацию */
  let snapTarget = null;

  const snapTo = (y) => {
    snapTarget = y;
    smoothScrollTo(y, 0);
  };

  /*
    Колесо перехватываем на захвате раньше Lenis: над каталогом оно не двигает
    страницу на шаг, а дотягивает до каталога целиком. Ниже каталога колесо
    отдаём Lenis, там обычный скролл.
  */
  const onWheel = (event) => {
    if (!window.lenis || !snapEnabled()) return;
    const zoneEnd = catalogSnapY();
    if (zoneEnd === null) return;

    const y = window.scrollY;
    const aboveCatalog = y < zoneEnd - SNAP_TOLERANCE_PX;
    const belowTop = y > SNAP_TOLERANCE_PX;
    const atOrAboveCatalog = y <= zoneEnd + SNAP_TOLERANCE_PX;

    let target = null;
    if (event.deltaY > 0 && aboveCatalog) target = zoneEnd;
    else if (event.deltaY < 0 && belowTop && atOrAboveCatalog) target = PAGE_TOP;
    if (target === null) return;

    event.preventDefault();
    event.stopImmediatePropagation();
    if (snapTarget !== target) snapTo(target);
  };

  /*
    Свайп: нативную инерцию перехватывать нельзя, поэтому ждём остановки и,
    если встали между экранами, дотягиваем по направлению движения.
  */
  let swipeFromY = null;
  let settleTimer = 0;

  const onTouchMove = () => {
    if (swipeFromY === null) swipeFromY = window.scrollY;
  };

  const onSettle = () => {
    snapTarget = null;
    if (swipeFromY === null) return;
    const fromY = swipeFromY;
    swipeFromY = null;
    if (!snapEnabled()) return;

    const zoneEnd = catalogSnapY();
    const y = window.scrollY;
    if (zoneEnd === null || y <= SNAP_TOLERANCE_PX || y >= zoneEnd - SNAP_TOLERANCE_PX) return;
    snapTo(y > fromY ? zoneEnd : PAGE_TOP);
  };

  window.addEventListener('wheel', onWheel, { capture: true, passive: false });
  window.addEventListener('touchmove', onTouchMove, { passive: true });
  window.addEventListener(
    'scroll',
    () => {
      clearTimeout(settleTimer);
      settleTimer = setTimeout(onSettle, SETTLE_MS);
    },
    { passive: true }
  );
})();
